import { lerp } from '@goodboydigital/odie';
import gsap from 'gsap';
import { Container, NineSlicePlane, Sprite, Text } from 'pixi.js';

import { Shared } from '../App';
import { Game } from '../game/Game';
import { StrUtils } from '../utils/StrUtils';
import { CharacterPin } from './CharacterPin';
import { Widget } from './Widget';

export class RaceWidget extends Widget
{
    public game: Game;
    public content: Container;
    public base: Sprite;
    public fill: NineSlicePlane;
    public playerPin: CharacterPin;
    public ghostPins: CharacterPin[];
    public label: Text;
    public raceIndexLabel: Text;
    public range: number;
    public ghostPinsContainer: Container;

    private _ratio = 0;
    private _raceIndex = 0;
    private _racePosition = 0;

    constructor(game: Game)
    {
        super();
        this.game = game;

        this.content = new Container();
        this.view.addChild(this.content);

        this.base = Sprite.from(Shared.assets.getTexture('progress-empty'));
        this.base.anchor.set(0.5);
        this.content.addChild(this.base);

        this.range = this.base.width * 0.94;

        this.fill = new NineSlicePlane(Shared.assets.getTexture('progress-finished'), 19, 19, 19, 19);
        this.content.addChild(this.fill);
        this.fill.x = -this.base.width / 2;
        this.fill.y = -this.base.height / 2;

        this.raceIndexLabel = new Text('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 16,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
        });
        this.raceIndexLabel.y = -30;
        this.raceIndexLabel.resolution = 2;
        this.raceIndexLabel.anchor.set(0.5);
        this.content.addChild(this.raceIndexLabel);

        this.ghostPins = [];
        this.ghostPinsContainer = new Container();
        this.content.addChild(this.ghostPinsContainer);
        this.ghostPinsContainer.sortableChildren = true;

        this.playerPin = new CharacterPin();
        this.content.addChild(this.playerPin);

        this.label = new Text('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'left',
            fontSize: 40,
            stroke: 0x333333,
            strokeThickness: 12,
            lineJoin: 'round',
        });
        this.label.resolution = 2;
        this.label.anchor.set(0, 0.5);
        this.label.y = -3;
        this.label.x = this.right + 5;
        this.content.addChild(this.label);
    }

    protected async onOpen(): Promise<void>
    {
        this._ratio = this.game.race.courseRatio;
        this._racePosition = -1;
        this._raceIndex = -1;
        this.refresh();
        this.updateGhostPins(false);
        gsap.killTweensOf(this.content);
        this.content.y = -300;
        await gsap.to(this.content, { y: 0, duration: 0.2, ease: 'expo.out' });
    }

    protected async onClose(): Promise<void>
    {
        gsap.killTweensOf(this.content);
        await gsap.to(this.content, { y: -300, duration: 0.2, ease: 'expo.in' });
    }

    public refresh(): void
    {
        if (!this.label) return;

        this._ratio = lerp(this._ratio, this.game.race.courseRatio, 0.25);
        this.fill.width = (this.range * this._ratio) + (this.base.width - this.range);
        // this.playerPin.x = this.fill.width - (this.base.width * 0.5);
        this.playerPin.x = this.positionByRatio(this._ratio);

        if (this._racePosition !== this.game.race.position)
        {
            this._racePosition = this.game.race.position;
            this.label.text = StrUtils.formatPosition(this.game.race.position, this.game.race.players);
            this.label.renderable = this.game.race.players > 1;
        }

        if (this._raceIndex !== this.game.race.raceIndex)
        {
            this._raceIndex = this.game.race.raceIndex;
            this.raceIndexLabel.text = this.game.race.getRaceName();
            console.log(this.game.race.getRaceName())
            this.playerPin.setCharacter(this.game.garage.selectedCharacter || '');
        }

        this.updateGhostPins(true);
    }

    private updateGhostPins(animated = false): void
    {
        const len = Math.max(this.ghostPins.length, this.game.race.ghosts.length);

        for (let i = 0; i < len; i++)
        {
            if (!this.ghostPins[i])
            {
                this.ghostPins[i] = new CharacterPin();
                this.ghostPins[i].scale.set(0.75);
                this.ghostPinsContainer.addChild(this.ghostPins[i]);
            }

            const pin = this.ghostPins[i];
            const ghost = this.game.race.ghosts[i];

            if (!ghost)
            {
                pin.setCharacter('');
                continue;
            }
            else
            {
                pin.setCharacter(ghost.character.data?.id ?? '', false);
                const ratio = this.game.race.getGhostCourseRatio(i);

                pin.x = lerp(pin.x, this.positionByRatio(ratio), animated ? 0.25 : 1);
                pin.zIndex = pin.x;
            }
        }
    }

    private positionByRatio(ratio: number): number
    {
        return (this.range * ratio) - (this.range * 0.5);
    }
}
